// dependencies
import React, { useRef, useState } from "react";
import useDeepCompareEffect from "use-deep-compare-effect";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import Button from "../../../../../components/Button";
import Chip from "../../../../../components/Chip";
import { Grid, GridCol } from "../../../../../components/Grid";
//components
import QueryBuilder from "../../../../../components/QueryBuilder";
import { IQueryField } from "../../../../../components/QueryBuilder/QueryBuilderUtils";
import QueryCard from "../../../../../components/QueryBuilder/components/QueryCard";
import Spacer from "../../../../../components/Spacer";
import ComponentLoader from "../../../../../components/Spinner/ComponentLoader";
// utils
import {
	DynamicCohortQuery,
	IS_MANAGER_FIELD,
	filterEmployeeFieldsForDynamicCohortQuery,
	formatQueryForBuilder,
	formatQueryForSave,
} from "./DynamicEmployeesUtils";

// hooks
import { useEmployeeFields } from "../../../../../api/hooks";
import { useListState } from "../../../../../hooks/useListState/useListState";
import useToast from "../../../../../hooks/useToast";

// css
import css from "./DynamicEmployees.module.scss";

/**
 * This component is used to build a dynamic cohort query
 * it leverages the QueryBuilder component to build the query
 * In the Utils we convert back and forth from these two data structures depending on the context
 *
 * For QueryBuilder we use:
 * query = {[[ IQuery]]}
 * i.e. an array of arrays of queries
 *
 * For Cohort Query we use:
 * query = {
 * 	and: [
 * 		{or: [
 * 			{field: {oid: string}, value: {oid: string} | number | string, comparator: string}
 * 		]}
 * 	]
 * }
 */

declare interface DynamicEmployeesQueryBuilderProps {
	goBack: () => void;
	existingDynamicEmployeesQuery?: DynamicCohortQuery;
	onSave: (query: DynamicCohortQuery) => void;
	isEdit?: boolean;
}

const DynamicEmployeesQueryBuilder = ({
	goBack,
	existingDynamicEmployeesQuery,
	onSave,
	isEdit = false,
}: DynamicEmployeesQueryBuilderProps) => {
	const toast = useToast();
	const { employeeFields, isLoading: isEmployeeFieldsLoading } = useEmployeeFields();
	const {
		list: currentAndQueryListForSave,
		listWithUniqueIds: currentAndQueryList,
		append: appendCurrentQuery,
		remove: removeCurrentQuery,
		update: updateCurrentQuery,
		setList: setCurrentAndQueryList,
		getIndexFromUniqueId,
	} = useListState([]);

	const [isSaving, setIsSaving] = useState(false);
	const [isLoadingEditData, setIsLoadingEditData] = useState(existingDynamicEmployeesQuery ? true : false);
	const [selectedOrQueryListId, setSelectedOrQueryListId] = useState<number | null>(null);
	const queryBuilderRef = useRef<HTMLDivElement | null>(null);
	const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
	const filteredFields = filterEmployeeFieldsForDynamicCohortQuery(employeeFields);
	filteredFields.push(IS_MANAGER_FIELD);
	filteredFields.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

	const handleSave = async () => {
		setIsSaving(true);
		try {
			const formattedQuery = formatQueryForSave(currentAndQueryListForSave);
			await onSave(formattedQuery);
		} catch (error) {
			toast("There was a problem saving Dynamic Cohort selection criteria.", {
				type: "error",
				size: "large",
			});
		}
		setIsSaving(false);
	};

	useDeepCompareEffect(() => {
		const loadEditQueryData = async () => {
			if (existingDynamicEmployeesQuery && !isEmployeeFieldsLoading) {
				setIsLoadingEditData(true);
				try {
					const formattedQuery = await formatQueryForBuilder(existingDynamicEmployeesQuery, employeeFields);
					setCurrentAndQueryList(formattedQuery);
				} catch (error) {
					toast("There was a problem getting Dynamic Cohort selection criteria.", {
						type: "error",
						size: "large",
					});

					goBack();
				}

				setIsLoadingEditData(false);
			}
		};

		loadEditQueryData();
	}, [employeeFields, existingDynamicEmployeesQuery]);

	const handleAddCondition = (orQueryCondition: Array<any>) => {
		if (selectedOrQueryListId === null) {
			appendCurrentQuery(orQueryCondition);
		} else {
			const index = getIndexFromUniqueId(selectedOrQueryListId);
			updateCurrentQuery(index, orQueryCondition);
			setSelectedOrQueryListId(null);
		}
	};

	const handleRemoveCondition = (listId: number, index: number) => {
		if (selectedOrQueryListId === listId) {
			setSelectedOrQueryListId(null);
		}

		removeCurrentQuery(index);
	};

	if (isLoadingEditData || isEmployeeFieldsLoading) {
		return (
			<div>
				<ComponentLoader />
			</div>
		);
	}

	return (
		<div className={css.dynamicEmployees}>
			<Grid gap={3} cols={2} className={css.body}>
				<GridCol span={1}>
					<div className={css.queryBuilderCardColumn} key={"query-builder-column"} ref={queryBuilderRef}>
						<QueryBuilder
							selectionFields={filteredFields as IQueryField[]}
							addCondition={(orQueryCondition: Array<any>) => handleAddCondition(orQueryCondition)}
							canQueryOnMultipleFields={false}
							editOrQuery={
								currentAndQueryList.find((query) => query.uniqueListId === selectedOrQueryListId)?.state
							}
						/>
					</div>
				</GridCol>
				<GridCol className={css.queryCardsColumn} span={1}>
					{currentAndQueryList.map(({ state: query, uniqueListId }, index) => (
						<div key={`query-card-${uniqueListId}`}>
							<div ref={(ref) => (cardRefs[uniqueListId] = ref)}>
								<QueryCard
									title="Employee Field"
									orQueryArray={query}
									removeOrQueryArray={() => handleRemoveCondition(uniqueListId, index)}
									onClick={() => setSelectedOrQueryListId(uniqueListId)}
									isSelected={selectedOrQueryListId === uniqueListId}
									onOutsideClick={(event) => {
										const isInCards = cardRefs.current.some(
											(ref) => ref && ref.contains(event.target)
										);
										if (
											queryBuilderRef.current &&
											!queryBuilderRef.current.contains(event.target) &&
											!isInCards
										) {
											setSelectedOrQueryListId(null);
										}
									}}
								/>
							</div>
							<Spacer height={2} />
							{index !== currentAndQueryList.length - 1 && (
								<div>
									<Chip label="AND" />
									<Spacer height={2} />
								</div>
							)}
						</div>
					))}
				</GridCol>
			</Grid>
			<Spacer height={7} />
			<Grid gap={1} cols={2} className={css.buttonWrapper}>
				<GridCol span={1}>
					<Button href="" className={css.backButton} color="blue" onClick={goBack}>
						Back
					</Button>
				</GridCol>

				<GridCol span={1} className={css.saveButton}>
					<Button
						color="green"
						onClick={handleSave}
						isRequesting={isSaving}
						disabled={currentAndQueryList.length < 1}
						icon={!isEdit ? faPlus : undefined}
					>
						{isEdit ? "Save Changes" : "Create Cohort"}
					</Button>
				</GridCol>
			</Grid>
		</div>
	);
};

export default DynamicEmployeesQueryBuilder;
